import React, { useContext } from 'react';
import pageClassNames from './HomePageView.module.css'
import { observer } from 'mobx-react-lite';
import { License, permissionsDeniedByLicense, SignedLicense, DomainScopedPermissionDescriptions } from '../State/EvidenceDB/License';
import { DbLicensesTable } from '../State/EvidenceDB/DbLicensesTable';
import { GlobalContext } from '../components/ExtensionWindow/GlobalContext';
import { UUID } from '../State/EvidenceDB/DatabaseTypes';
import { ProductName } from "../Styles/ProductName";
import classNames from "./LicensePageView.module.css";
import commonClassNames from "../Styles/common.module.css"
import { Literal } from '../Styles/ReportStyles';


const tryAddLicense = async (eventTarget: EventTarget & HTMLTextAreaElement) => {
	try {
		// THe JSON parser will throw an exception if the text field isn't valid JSON and we'll return false.
		const o = JSON.parse(eventTarget.value.trim()) as SignedLicense;
		if (typeof o !== "object") {
			return false;
		}
		// The signature validation required by the license table API will throw an exception
		// if the license isn't valid
		await DbLicensesTable.addLicense(o as SignedLicense);
		// After successfully adding a license (without throwing) we can clear the 
		// text area so it can be used again.
		eventTarget.value = '';
		return true;
	} catch {
		return false;
	}
}

const deleteLicense = async (licenseUuid: UUID) => {
	if (window.confirm("Are you sure you want to delete this license?")) {
		await DbLicensesTable.deleteLicense({licenseUuid})
	}
}

export const ProfessionalEditionFeatureList = () => (
	<ul>
		<li>exporting of network traffic, instrumented page events, and other data collected,</li>
		<li>search of data received (inbound traffic),</li>
		<li>additional context and details elucidating where shared data were discovered,</li>
		<li>causation chain analyses that elucidate how and why data were shared, and</li>
		<li>specialized reports focused on specific categories of privacy violations.</li>
	</ul>
)
export const LicenseView = ({license}: {license: License}) => {
	const {validOnlyAtDomainSuffixes, licenseUuid, clientName, whenIssuedIso, whenExpiresIso, languageText} = license;
	const deniedPermissions = permissionsDeniedByLicense(license);
	return (
		<div className={classNames.LicenseElement}>
				<button className={`${classNames.DeleteButton} ${commonClassNames.borderless}`} onClick={ e => { e.preventDefault(); deleteLicense(licenseUuid)}}>x</button>
			<div>{clientName}</div>
			<div className={classNames.LicenseItem}>
				<label>Valid at</label>
				<div>{ validOnlyAtDomainSuffixes == null ? (<>all websites</>) : (<Literal>
					{validOnlyAtDomainSuffixes.join(", ")}
				</Literal>) }</div>
			</div>
			{ deniedPermissions.length === 0 ? (
				<div className={classNames.LicenseItem}>
					<label>Allows</label>
					<div>All features and uses</div>
				</div>
			) : (
				<div className={classNames.LicenseItem}>
					<label>Excludes</label>
					<div>{ deniedPermissions.map( p => DomainScopedPermissionDescriptions[p]).join(', ') }</div>
				</div>
			)}
			<div className={classNames.LicenseItem}>
				<label>Issued</label>
				<div>{ new Date(whenIssuedIso).toLocaleDateString() }</div>
			</div>
			<div className={classNames.LicenseItem}>
				<label>Expires</label>
				<div>{ new Date(whenExpiresIso).toLocaleDateString() }</div>
			</div>
			{ languageText == null ? null : (
				<div className={classNames.LicenseItem}>
					<div className={classNames.LicenseText}>{languageText}</div>
				</div>
			)}
		</div>
	);
}

export const LicensePageView = observer( () => {
	const globalContext = useContext(GlobalContext);
	if (globalContext == null) return null;
	const {licenseCache} = globalContext;
	return (		
		<div className={pageClassNames.SingleColumnPageContainerElement}><div className={pageClassNames.SingleColumnLeftJustifiedWidthLimited}>
			<h1><ProductName/>{ licenseCache.isLicensed ? ' License' : ` Free Edition License`}</h1>
			<p>
				This software is provided “as is”, without warranty of any kind, express or implied, including but not limited to the warranties of merchantability, fitness for a particular purpose, and noninfringement. In no event shall the authors or copyright holders be liable for any claim, damages, or other liability, whether in an action of contract, tort, or otherwise, arising from, out of or in connection with the software or the use or dealings in the software.
			</p>
			{ licenseCache.validClientNames.length <= 1 ? null : (
				<div className={classNames.LabeledValueLeftJustified}>
					<label>Current client</label>
					<select value={licenseCache.activeClientName} onChange={e => licenseCache.setActiveClientName(e.currentTarget.value)} >{
						licenseCache.validClientNames.map( clientName => (
							<option key={clientName} value={clientName}>{clientName}</option>
						))
					}</select>
				</div>
			)}
			{ !licenseCache.isLicensed ? null : (
				<div className={classNames.LabeledValueLeftJustified}>
					<label>Your Licenses</label>
					{ licenseCache.nonExpiredLicensesForAllClients.map( license => {						
						return (<LicenseView key={license.licenseUuid} license={license} />);
					})}
				</div>
			)}
			<div className={classNames.LabeledValueCentered} style={{marginTop: '2rem'}}>
				<textarea className={classNames.LicenseTextAreaElement} onChange={ e => tryAddLicense(e.currentTarget) } ></textarea>
				<label>If you have { licenseCache.isLicensed ? 'an additional' : 'a' } license, add it by pasting it above.</label>
			</div>
		</div></div>
	);
});
